import { useCallback, useEffect, useState } from "react";

import { fetchBlocks, unblockUser } from "../../Services/SocialService";
import type { BlockedUser } from "../../data/types";

/**
 * Blockerade användare, med ett sätt att häva blockeringen.
 *
 * Blockeringen görs där man ser personen (profilens meny). Det här är
 * vägen tillbaka. En blockering rev följningarna åt båda håll, så att
 * häva den återställer inte dem — det säger bekräftelsen rakt ut.
 */
export default function BlockedUsersSection() {
  const [items, setItems] = useState<BlockedUser[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string>();

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setItems(await fetchBlocks());
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unblock = async (user: BlockedUser) => {
    if (busyId) return;

    setBusyId(user.id);
    setActionError(undefined);

    try {
      await unblockUser(user.id);
      // Direkt ur listan, utan en ny hämtning att vänta på.
      setItems((current) => (current ?? []).filter((b) => b.id !== user.id));
      setConfirmingId(null);
    } catch {
      setActionError(`Could not unblock ${user.displayName}. Try again.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="settings-section" aria-labelledby="blocked-heading">
      <h2 id="blocked-heading">Blocked users</h2>

      {loadError && (
        <>
          <p role="alert">Could not load your blocked users.</p>
          <div className="settings-actions">
            <button type="button" className="login-button" onClick={load}>
              Try again
            </button>
          </div>
        </>
      )}

      {!loadError && items === null && <p role="status">Loading…</p>}

      {items && items.length === 0 && (
        <p>
          Nobody is blocked. You can block someone from the menu on their
          profile.
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="settings-blocked-list">
          {items.map((user) => (
            <li key={user.id} className="settings-blocked-row">
              <strong>{user.displayName}</strong>

              {confirmingId === user.id ? (
                <div className="settings-blocked-confirm">
                  <span>
                    Unblock {user.displayName}? You will be able to see each
                    other&apos;s check-ins again, but you won&apos;t follow each
                    other.
                  </span>

                  <div className="settings-actions">
                    <button
                      type="button"
                      className="login-button"
                      disabled={busyId === user.id}
                      onClick={() => unblock(user)}
                    >
                      {busyId === user.id ? "Unblocking…" : "Confirm unblock"}
                    </button>
                    <button
                      type="button"
                      className="login-button settings-cancel"
                      disabled={busyId === user.id}
                      onClick={() => setConfirmingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="login-button settings-cancel"
                  onClick={() => {
                    setActionError(undefined);
                    setConfirmingId(user.id);
                  }}
                >
                  Unblock
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {actionError && <p role="alert">{actionError}</p>}
    </section>
  );
}
